import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import LeadNote from "@/components/LeadNote";
import GalleryAdder from "@/components/GalleryAdder";
import DictateButton from "@/components/DictateButton";
import { Users, ChefHat, Utensils, IndianRupee, Plus, Check, X, Pencil, Trash2, Calendar as CalIcon, Mail, Phone, ChevronLeft, ChevronRight, Download, MessageCircle } from "lucide-react";
import { formatINR } from "@/components/ChefCard";
import ImageUpload from "@/components/ImageUpload";

export default function AdminPortal() {
  const { user, setAuthOpen } = useAuth();
  const [stats, setStats] = useState(null);
  const [chefs, setChefs] = useState([]);
  const [users, setUsers] = useState([]);
  const [menus, setMenus] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadSource, setLeadSource] = useState("all");

  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({
    title: "", tagline: "", description: "", image_url: "", chef_credit: "",
    courses: "", event_date: "", event_time: "7:30 PM onwards",
    cover_charge: 1999, total_seats: 20, tags: "", featured: false,
  });
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventEditForm, setEventEditForm] = useState(null);
  const [eventSaving, setEventSaving] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date());
  const [showManual, setShowManual] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualForm, setManualForm] = useState({ service_type: "private_chef", customer_name: "", customer_phone: "", city: "", chef_id: "", menu_id: "", event_table_id: "", date: "", guests: "", total_amount: "", status: "confirmed", notes: "" });

  const [newMenu, setNewMenu] = useState({
    title: "", tagline: "", description: "", image_url: "", category: "",
    courses: "", chef_credit: "", price_per_head: 0, min_guests: 6, tags: "", featured: false,
  });
  const [editingMenu, setEditingMenu] = useState(null);
  const [menuEditForm, setMenuEditForm] = useState(null);
  const [menuEditSaving, setMenuEditSaving] = useState(false);

  // Chef edit dialog state
  const [editingChef, setEditingChef] = useState(null);
  const [chefEditForm, setChefEditForm] = useState(null);
  const [chefBookings, setChefBookings] = useState([]);
  const [chefEditSaving, setChefEditSaving] = useState(false);
  const [chefAppointmentsOpen, setChefAppointmentsOpen] = useState(null);
  const [chefAppointments, setChefAppointments] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [calDay, setCalDay] = useState(null);
  const [settings, setSettings] = useState({ legal_name: "", gstin: "", address: "", email: "", phone: "", invoice_prefix: "", agreement_prefix: "" });
  const [settingsSaving, setSettingsSaving] = useState(false);

  const load = async () => {
    try {
      const [s, c, u, m, b, mem, lds, evs] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/chefs"),
        api.get("/admin/users"),
        api.get("/menus", { params: { active_only: false } }),
        api.get("/bookings"),
        api.get("/admin/memberships"),
        api.get("/admin/leads"),
        api.get("/chefs-table", { params: { active_only: false } }),
      ]);
      setStats(s.data);      setChefs(c.data.chefs);
      setUsers(u.data.users);
      setMenus(m.data.menus);
      setBookings(b.data.bookings);
      setMemberships(mem.data.memberships);
      setLeads(lds.data.leads);
      setEvents(evs.data.events);
      try {
        const st = await api.get("/admin/settings");
        setSettings(st.data.settings);
      } catch { /* manager without access */ }
    } catch (e) {
      // 403 for non-admin
    }
  };

  useEffect(() => { if (user?.role === "admin" || user?.role === "manager") load(); }, [user]);

  const saveLeadNote = async (id, notes) => {
    try {
      await api.patch(`/admin/leads/${id}`, { notes });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, notes } : l)));
      toast.success("Note saved");
    } catch (e) { toast.error("Failed to save note"); }
  };

  const waLink = (l) => {
    const phone = (l.phone || "").replace(/\D/g, "").replace(/^0+/, "");
    const num = phone.length === 10 ? `91${phone}` : phone;
    const first = (l.name || "there").split(" ")[0];
    const subject = (l.message || "").split("—")[0].trim().slice(0, 60);
    const text = encodeURIComponent(
      `Namaste ${first}, this is Chef Hire. Thank you for your enquiry${subject ? ` about ${subject}` : ""}. ` +
      `May I share the menu and the quote with you?`
    );
    return `https://wa.me/${num}?text=${text}`;
  };

  const changeLeadStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch { /* ignore */ }
  };

  const changeMembershipStatus = async (id, status) => {
    try {
      await api.patch(`/admin/memberships/${id}`, { status });
      setMemberships((ms) => ms.map((m) => (m.id === id ? { ...m, status } : m)));
    } catch (e) {
      // ignore
    }
  };

  const setChefStatus = async (id, status) => {
    try {
      await api.patch(`/admin/chefs/${id}/status`, { status });
      toast.success(`Chef ${status}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/admin/users/${uid}/role`, { role });
      toast.success("Role updated");
      load();
    } catch { toast.error("Failed"); }
  };

  const createMenu = async () => {
    if (!newMenu.title || !newMenu.image_url) return toast.error("Title and image required");
    try {
      const payload = {
        ...newMenu,
        courses: newMenu.courses.split(",").map(s => s.trim()).filter(Boolean),
        tags: newMenu.tags.split(",").map(s => s.trim()).filter(Boolean),
        price_per_head: parseInt(newMenu.price_per_head) || 0,
        min_guests: parseInt(newMenu.min_guests) || 6,
      };
      await api.post("/menus", payload);
      toast.success("Menu created");
      setNewMenu({ title: "", tagline: "", description: "", image_url: "", category: "", courses: "", chef_credit: "", price_per_head: 0, min_guests: 6, tags: "", featured: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const assignChef = async (bookingId, chefId) => {
    try {
      await api.patch(`/bookings/${bookingId}/assign`, { chef_id: chefId });
      toast.success("Chef assigned");
      load();
    } catch { toast.error("Failed"); }
  };

  const toggleMenuActive = async (menu) => {
    try {
      await api.patch(`/menus/${menu.id}`, { active: !menu.active });
      load();
    } catch { toast.error("Failed"); }
  };

  const openMenuEdit = (m) => {
    setEditingMenu(m);
    setMenuEditForm({
      title: m.title || "", tagline: m.tagline || "", category: m.category || "",
      image_url: m.image_url || "", description: m.description || "",
      courses: (m.courses || []).join(", "), chef_credit: m.chef_credit || "",
      price_per_head: m.price_per_head || 0, min_guests: m.min_guests || 1,
      tags: (m.tags || []).join(", "), featured: !!m.featured,
    });
  };

  const saveMenuEdit = async () => {
    if (!editingMenu) return;
    setMenuEditSaving(true);
    try {
      const f = menuEditForm;
      const payload = {
        title: f.title, tagline: f.tagline, category: f.category, image_url: f.image_url,
        description: f.description, chef_credit: f.chef_credit,
        price_per_head: Number(f.price_per_head) || 0, min_guests: Number(f.min_guests) || 1,
        featured: !!f.featured,
        courses: f.courses.split(",").map((s) => s.trim()).filter(Boolean),
        tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.patch(`/menus/${editingMenu.id}`, payload);
      toast.success("Menu updated");
      setEditingMenu(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setMenuEditSaving(false); }
  };

  const createEvent = async () => {
    if (!newEvent.title || !newEvent.image_url) return toast.error("Title and image required");
    try {
      const payload = {
        ...newEvent,
        courses: newEvent.courses.split(",").map(s => s.trim()).filter(Boolean),
        tags: newEvent.tags.split(",").map(s => s.trim()).filter(Boolean),
        cover_charge: parseInt(newEvent.cover_charge) || 0,
        total_seats: parseInt(newEvent.total_seats) || 0,
        price_on_request: !!newEvent.price_on_request,
        inclusions: (newEvent.inclusions || "").split("|").map(s => s.trim()).filter(Boolean),
        exclusions: (newEvent.exclusions || "").split("|").map(s => s.trim()).filter(Boolean),
      };
      await api.post("/chefs-table", payload);
      toast.success("Chef's Table event created");
      setNewEvent({ title: "", tagline: "", description: "", image_url: "", chef_credit: "", courses: "", event_date: "", event_time: "7:30 PM onwards", cover_charge: 1999, total_seats: 20, tags: "", featured: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleEventActive = async (ev) => {
    try { await api.patch(`/chefs-table/${ev.id}`, { active: !ev.active }); load(); }
    catch { toast.error("Failed"); }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/chefs-table/${ev.id}`); toast.success("Event removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openEventEdit = (ev) => {
    setEditingEvent(ev);
    setEventEditForm({
      title: ev.title || "", tagline: ev.tagline || "", description: ev.description || "",
      image_url: ev.image_url || "", chef_credit: ev.chef_credit || "",
      courses: (ev.courses || []).join(", "),
      inclusions: (ev.inclusions || []).join(" | "),
      exclusions: (ev.exclusions || []).join(" | "),
      gallery: ev.gallery || [],
      price_on_request: ev.price_on_request !== false,
      event_date: ev.event_date || "", event_time: ev.event_time || "",
      cover_charge: ev.cover_charge || 0, total_seats: ev.total_seats || 0,
      seats_booked: ev.seats_booked || 0,
      tags: (ev.tags || []).join(", "), featured: !!ev.featured,
    });
  };

  const saveEventEdit = async () => {
    if (!editingEvent) return;
    setEventSaving(true);
    try {
      const f = eventEditForm;
      const payload = {
        title: f.title, tagline: f.tagline, description: f.description, image_url: f.image_url,
        chef_credit: f.chef_credit, event_date: f.event_date, event_time: f.event_time,
        cover_charge: Number(f.cover_charge) || 0, total_seats: Number(f.total_seats) || 0,
        seats_booked: Number(f.seats_booked) || 0,
        price_on_request: !!f.price_on_request,
        courses: f.courses.split(",").map(s => s.trim()).filter(Boolean),
        inclusions: (f.inclusions || "").split("|").map(s => s.trim()).filter(Boolean),
        exclusions: (f.exclusions || "").split("|").map(s => s.trim()).filter(Boolean),
        gallery: f.gallery || [],
        tags: f.tags.split(",").map(s => s.trim()).filter(Boolean),
        featured: !!f.featured,
      };
      await api.patch(`/chefs-table/${editingEvent.id}`, payload);
      toast.success("Event updated");
      setEditingEvent(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setEventSaving(false); }
  };

  const submitManual = async () => {
    if (!manualForm.customer_name.trim() || !manualForm.date) return toast.error("Customer name and date are required");
    setManualSaving(true);
    try {
      const payload = { ...manualForm, guests: manualForm.guests ? parseInt(manualForm.guests) : null, total_amount: parseFloat(manualForm.total_amount) || 0 };
      await api.post("/bookings/manual", payload);
      toast.success("Booking added to the calendar");
      setShowManual(false);
      setManualForm({ service_type: "private_chef", customer_name: "", customer_phone: "", city: "", chef_id: "", menu_id: "", event_table_id: "", date: "", guests: "", total_amount: "", status: "confirmed", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add booking"); }
    finally { setManualSaving(false); }
  };

  const exportCsv = async (kind, params = {}) => {
    try {
      const r = await api.get(`/admin/export/${kind}.csv`, { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `chefhire-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      const r = await api.put("/admin/settings", settings);
      setSettings(r.data.settings);
      toast.success("Invoice settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSettingsSaving(false); }
  };

  const changeBookingStatus = async (bookingId, status) => {
    try {
      await api.patch(`/bookings/${bookingId}/status`, { status });
      toast.success("Status updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update status"); }
  };

  const bookingsByDate = () => {
    const map = {};
    (bookings || []).forEach((b) => {
      const d = (b.start_date || b.event_date || "").slice(0, 10);
      if (!d) return;
      (map[d] = map[d] || []).push(b);
    });
    return map;
  };

  const openChefEdit = (chef) => {
    setEditingChef(chef);
    setChefEditForm({
      name: chef.name || "",
      photo_url: chef.photo_url || "",
      bio: chef.bio || "",
      cuisines: (chef.cuisines || []).join(", "),
      signature_dishes: (chef.signature_dishes || []).join(", "),
      experience_yrs: chef.experience_yrs || 0,
      languages: (chef.languages || []).join(", "),
      diet_tags: (chef.diet_tags || []).join(", "),
      cities: (chef.cities || []).join(", "),
      salary_type: chef.salary_type || "indian",
      monthly_salary: chef.monthly_salary || 0,
      trial_fee: chef.trial_fee || 0,
      gallery: (chef.gallery || []).join(", "),
      rating: chef.rating ?? 4.9,
      active: chef.active !== false,
    });
  };

  const saveChefEdit = async () => {
    if (!editingChef) return;
    setChefEditSaving(true);
    try {
      const payload = {
        name: chefEditForm.name,
        photo_url: chefEditForm.photo_url,
        bio: chefEditForm.bio,
        cuisines: chefEditForm.cuisines.split(",").map(s => s.trim()).filter(Boolean),
        signature_dishes: chefEditForm.signature_dishes.split(",").map(s => s.trim()).filter(Boolean),
        experience_yrs: parseInt(chefEditForm.experience_yrs) || 0,
        languages: chefEditForm.languages.split(",").map(s => s.trim()).filter(Boolean),
        diet_tags: chefEditForm.diet_tags.split(",").map(s => s.trim()).filter(Boolean),
        cities: chefEditForm.cities.split(",").map(s => s.trim()).filter(Boolean),
        salary_type: chefEditForm.salary_type,
        monthly_salary: parseInt(chefEditForm.monthly_salary) || 0,
        trial_fee: parseInt(chefEditForm.trial_fee) || 0,
        gallery: chefEditForm.gallery.split(",").map(s => s.trim()).filter(Boolean),
        rating: parseFloat(chefEditForm.rating) || 4.9,
        active: chefEditForm.active,
      };
      await api.patch(`/admin/chefs/${editingChef.id}`, payload);
      toast.success("Chef updated");
      setEditingChef(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setChefEditSaving(false); }
  };

  const toggleChefActive = async (chef) => {
    try {
      await api.patch(`/admin/chefs/${chef.id}/active`, { active: !(chef.active !== false) });
      toast.success(chef.active !== false ? "Chef deactivated (hidden from public)" : "Chef re-activated");
      load();
    } catch { toast.error("Failed"); }
  };

  const deleteChef = async (chef) => {
    if (!window.confirm(`Delete ${chef.name}? This cannot be undone. Chefs with active bookings cannot be deleted.`)) return;
    try {
      await api.delete(`/admin/chefs/${chef.id}`);
      toast.success("Chef removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const openChefAppointments = async (chef) => {
    setChefAppointmentsOpen(chef);
    try {
      const r = await api.get(`/admin/chefs/${chef.id}/bookings`);
      setChefAppointments(r.data.bookings);
    } catch { toast.error("Failed to load appointments"); }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif-display text-4xl">Admin Portal</h1>
        <p className="text-neutral-400 mt-3">Sign in as an admin.</p>
        <Button onClick={() => setAuthOpen(true)} className="mt-6 rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px]">Sign in</Button>
      </div>
    );
  }
  if (user.role !== "admin" && user.role !== "manager") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif-display text-4xl">Restricted.</h1>
        <p className="text-neutral-400 mt-3">This portal is for admins & managers only.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="small-caps text-neutral-500 mb-3">Admin</div>
      <h1 className="font-serif-display text-5xl">Chef Hire — Operations</h1>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-10" data-testid="admin-stats">
        {[
          { icon: Users, label: "Users", value: stats?.users, color: "text-white" },
          { icon: ChefHat, label: "Chefs live", value: stats?.chefs_approved, color: "text-[#F5B041]" },
          { icon: ChefHat, label: "Pending", value: stats?.chefs_pending, color: "text-[#C04000]" },
          { icon: Utensils, label: "Bookings", value: stats?.bookings, color: "text-white" },
          { icon: IndianRupee, label: "Revenue", value: stats?.revenue_inr ? `₹${formatINR(stats.revenue_inr)}` : "₹0", color: "text-white" },
        ].map((s) => (
          <div key={s.label} className="bg-[#141414] border border-white/10 p-5">
            <div className="flex items-center justify-between">
              <s.icon size={14} className={s.color} />
              <div className="small-caps text-[9px] text-neutral-500">{s.label}</div>
            </div>
            <div className="font-serif-display text-3xl mt-3">{s.value ?? "—"}</div>
          </div>
        ))}
      </div>

      <div className="dotted-sep my-10" />

      <Tabs defaultValue="chefs" className="w-full">
        <TabsList className="grid grid-cols-10 md:w-[1360px] rounded-none bg-transparent border border-white/10 p-0 h-11">
          <TabsTrigger value="chefs" data-testid="admin-tab-chefs" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">Chefs</TabsTrigger>
          <TabsTrigger value="menus" data-testid="admin-tab-menus" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">Menus</TabsTrigger>
          <TabsTrigger value="chefs-table" data-testid="admin-tab-chefs-table" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">Chef's Table</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="admin-tab-calendar" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">Calendar</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">Bookings</TabsTrigger>
          <TabsTrigger value="crm" data-testid="admin-tab-crm" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">CRM</TabsTrigger>
          <TabsTrigger value="memberships" data-testid="admin-tab-memberships" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">Memberships</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">Users</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings" className="rounded-none small-caps text-[10px] data-[state=active]:bg-white/5 h-full">Settings</TabsTrigger>
        </TabsList>

        {/* CHEFS */}
        <TabsContent value="chefs" className="mt-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-chefs">
            {chefs.map((c) => (
              <div key={c.id} className={`card-lux p-5 ${c.active === false ? "opacity-60" : ""}`}>
                <div className="flex items-center gap-4">
                  <img src={c.photo_url} alt="" className="w-14 h-14 object-cover border border-white/10" />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-menu text-lg truncate">{c.name}</div>
                    <div className="text-xs text-neutral-500 truncate">{c.cuisines?.slice(0,2).join(" · ")}</div>
                  </div>
                </div>
                <div className="mt-3 space-y-1 text-xs text-neutral-300" data-testid={`chef-contact-${c.id}`}>
                  {c.contact_phone && (
                    <div className="flex items-center gap-2"><Phone size={11} className="text-[#F5B041] shrink-0" /> <a href={`tel:${c.contact_phone}`} className="hover:text-white">{c.contact_phone}</a></div>
                  )}
                  {c.contact_email && (
                    <div className="flex items-center gap-2 min-w-0"><Mail size={11} className="text-[#F5B041] shrink-0" /> <a href={`mailto:${c.contact_email}`} className="truncate hover:text-white">{c.contact_email}</a></div>
                  )}
                  {!c.contact_phone && !c.contact_email && (
                    <div className="text-neutral-600 italic">No contact on file</div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-4 gap-2 flex-wrap">
                  <span className={`chip capitalize ${c.status === "approved" ? "text-green-400 border-green-400/40" : c.status === "pending" ? "text-[#F5B041] border-[#F5B041]/40" : "text-red-400 border-red-400/40"}`}>{c.status}</span>
                  {c.active === false && <span className="chip text-red-400 border-red-400/40">Hidden</span>}
                  <div className="text-sm ml-auto">₹{formatINR(c.monthly_salary)}/mo</div>
                </div>
                <div className="flex gap-2 mt-4 flex-wrap">
                  {c.status !== "approved" && (
                    <Button size="sm" onClick={() => setChefStatus(c.id, "approved")} data-testid={`approve-${c.id}`}
                      className="rounded-none bg-[#C04000] hover:bg-[#A03500] h-9 small-caps text-[10px] flex-1">
                      <Check size={12} className="mr-1" /> Approve
                    </Button>
                  )}
                  {c.status !== "rejected" && c.status === "pending" && (
                    <Button size="sm" onClick={() => setChefStatus(c.id, "rejected")} variant="ghost"
                      className="rounded-none border border-white/10 h-9 small-caps text-[10px] hover:bg-white/5">
                      <X size={12} className="mr-1" /> Reject
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button onClick={() => openChefEdit(c)} data-testid={`edit-chef-${c.id}`}
                    className="border border-white/10 hover:bg-white/5 h-9 small-caps text-[10px] flex items-center justify-center gap-1 transition">
                    <Pencil size={11} /> Edit
                  </button>
                  <button onClick={() => openChefAppointments(c)} data-testid={`appointments-chef-${c.id}`}
                    className="border border-white/10 hover:bg-white/5 h-9 small-caps text-[10px] flex items-center justify-center gap-1 transition">
                    <CalIcon size={11} /> Appointments
                  </button>
                  <button onClick={() => toggleChefActive(c)} data-testid={`toggle-active-${c.id}`}
                    className={`border h-9 small-caps text-[10px] flex items-center justify-center transition ${c.active === false ? "border-[#F5B041] text-[#F5B041] hover:bg-[#F5B041]/10" : "border-white/10 hover:bg-white/5"}`}>
                    {c.active === false ? "Activate" : "Deactivate"}
                  </button>
                  <button onClick={() => deleteChef(c)} data-testid={`delete-chef-${c.id}`}
                    className="border border-red-400/30 text-red-400 hover:bg-red-400/10 h-9 small-caps text-[10px] flex items-center justify-center gap-1 transition">
                    <Trash2 size={11} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* MENUS */}
        <TabsContent value="menus" className="mt-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="small-caps text-neutral-500 mb-4">Existing menus</div>
              <div className="space-y-3" data-testid="admin-menus">
                {menus.map((m) => (
                  <div key={m.id} className="card-lux p-4 flex items-center gap-4">
                    <img src={m.image_url} className="w-16 h-16 object-cover border border-white/10" alt="" />
                    <div className="flex-1">
                      <div className="font-serif-menu">{m.title}</div>
                      <div className="text-xs text-neutral-500">₹{formatINR(m.price_per_head)}/head · {m.category}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={() => openMenuEdit(m)} data-testid={`edit-menu-${m.id}`} className="small-caps text-[10px] text-[#E7D8B5] hover:text-white">
                        Edit
                      </button>
                      <button onClick={() => toggleMenuActive(m)} className="small-caps text-[10px] text-neutral-400 hover:text-white">
                        {m.active ? "Hide" : "Publish"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="small-caps text-neutral-500 mb-4"><Plus size={12} className="inline mr-1" /> New menu</div>
              <div className="bg-[#141414] border border-white/10 p-6 space-y-3">
                <Input placeholder="Title" value={newMenu.title} onChange={(e) => setNewMenu({ ...newMenu, title: e.target.value })} data-testid="new-menu-title"
                  className="rounded-none bg-transparent border-white/10 h-11" />
                <Input placeholder="Tagline" value={newMenu.tagline} onChange={(e) => setNewMenu({ ...newMenu, tagline: e.target.value })}
                  className="rounded-none bg-transparent border-white/10 h-11" />
                <Input placeholder="Category (e.g. Kaiseki)" value={newMenu.category} onChange={(e) => setNewMenu({ ...newMenu, category: e.target.value })}
                  className="rounded-none bg-transparent border-white/10 h-11" />
                <ImageUpload value={newMenu.image_url} onChange={(url) => setNewMenu({ ...newMenu, image_url: url })} testId="new-menu-image" />
                <Textarea placeholder="Description" value={newMenu.description} onChange={(e) => setNewMenu({ ...newMenu, description: e.target.value })}
                  className="rounded-none bg-transparent border-white/10 min-h-20" />
                <Input placeholder="Courses (comma-separated)" value={newMenu.courses} onChange={(e) => setNewMenu({ ...newMenu, courses: e.target.value })}
                  className="rounded-none bg-transparent border-white/10 h-11" />
                <Input placeholder="Chef credit" value={newMenu.chef_credit} onChange={(e) => setNewMenu({ ...newMenu, chef_credit: e.target.value })}
                  className="rounded-none bg-transparent border-white/10 h-11" />
                <div className="grid grid-cols-2 gap-3">
                  <Input type="number" placeholder="Price/head" value={newMenu.price_per_head} onChange={(e) => setNewMenu({ ...newMenu, price_per_head: e.target.value })} data-testid="new-menu-price"
                    className="rounded-none bg-transparent border-white/10 h-11" />
                  <Input type="number" placeholder="Min guests" value={newMenu.min_guests} onChange={(e) => setNewMenu({ ...newMenu, min_guests: e.target.value })}
                    className="rounded-none bg-transparent border-white/10 h-11" />
                </div>
                <Input placeholder="Tags (comma-separated)" value={newMenu.tags} onChange={(e) => setNewMenu({ ...newMenu, tags: e.target.value })}
                  className="rounded-none bg-transparent border-white/10 h-11" />
                <Button onClick={createMenu} data-testid="create-menu-btn"
                  className="w-full rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px]">
                  Publish menu
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* CHEF'S TABLE */}
        <TabsContent value="chefs-table" className="mt-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="small-caps text-neutral-500 mb-4">Chef's Table menus · hosted at the guest's home</div>
              <div className="space-y-3" data-testid="admin-chefs-table">
                {events.length === 0 && <div className="text-neutral-500 text-sm">No events yet.</div>}
                {events.map((ev) => {
                  const left = (ev.total_seats || 0) - (ev.seats_booked || 0);
                  return (
                    <div key={ev.id} className={`card-lux p-4 flex items-center gap-4 ${ev.active === false ? "opacity-60" : ""}`}>
                      <img src={ev.image_url} className="w-16 h-16 object-cover border border-white/10" alt="" />
                      <div className="flex-1 min-w-0">
                        <div className="font-serif-menu truncate">{ev.title}</div>
                        <div className="text-xs text-neutral-500">
                          {ev.price_on_request ? "Rates on request" : `₹${formatINR(ev.cover_charge)}/guest`}
                        </div>
                        <div className="text-xs text-[#C9962F] mt-0.5" data-testid={`event-seats-${ev.id}`}>
                          {ev.price_on_request ? `Up to ${ev.total_seats || 0} guests` : `${ev.seats_booked || 0}/${ev.total_seats || 0} booked · ${left} left`}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <button onClick={() => openEventEdit(ev)} data-testid={`edit-event-${ev.id}`} className="small-caps text-[10px] text-[#E7D8B5] hover:text-white">Edit</button>
                        <button onClick={() => toggleEventActive(ev)} className="small-caps text-[10px] text-neutral-400 hover:text-white">{ev.active ? "Hide" : "Publish"}</button>
                        <button onClick={() => deleteEvent(ev)} data-testid={`delete-event-${ev.id}`} className="small-caps text-[10px] text-red-400 hover:text-red-300">Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="small-caps text-neutral-500 mb-4"><Plus size={12} className="inline mr-1" /> New Chef's Table</div>
              <div className="bg-[#141414] border border-white/10 p-6 space-y-3">
                <Input placeholder="Title" value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} data-testid="new-event-title" className="rounded-none bg-transparent border-white/10 h-11" />
                <Input placeholder="Tagline" value={newEvent.tagline} onChange={(e) => setNewEvent({ ...newEvent, tagline: e.target.value })} className="rounded-none bg-transparent border-white/10 h-11" />
                <ImageUpload value={newEvent.image_url} onChange={(url) => setNewEvent({ ...newEvent, image_url: url })} testId="new-event-image" />
                <Textarea placeholder="Description" value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} className="rounded-none bg-transparent border-white/10 min-h-20" />
                <Textarea placeholder="Courses (comma-separated)" value={newEvent.courses} onChange={(e) => setNewEvent({ ...newEvent, courses: e.target.value })} className="rounded-none bg-transparent border-white/10 min-h-16" />
                <Input placeholder="Chef credit" value={newEvent.chef_credit} onChange={(e) => setNewEvent({ ...newEvent, chef_credit: e.target.value })} className="rounded-none bg-transparent border-white/10 h-11" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Event date</Label>
                    <Input type="date" value={newEvent.event_date} onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })} data-testid="new-event-date" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Seating time</Label>
                    <Input value={newEvent.event_time} onChange={(e) => setNewEvent({ ...newEvent, event_time: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Cover charge (₹/seat)</Label>
                    <Input type="number" value={newEvent.cover_charge} onChange={(e) => setNewEvent({ ...newEvent, cover_charge: e.target.value })} data-testid="new-event-cover" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Total seats</Label>
                    <Input type="number" value={newEvent.total_seats} onChange={(e) => setNewEvent({ ...newEvent, total_seats: e.target.value })} data-testid="new-event-seats" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                </div>
                <Input placeholder="Tags (comma-separated)" value={newEvent.tags} onChange={(e) => setNewEvent({ ...newEvent, tags: e.target.value })} className="rounded-none bg-transparent border-white/10 h-11" />
                <Textarea placeholder="Inclusions (separate with |)" value={newEvent.inclusions || ""} onChange={(e) => setNewEvent({ ...newEvent, inclusions: e.target.value })} data-testid="new-event-inclusions" className="rounded-none bg-transparent border-white/10 min-h-16" />
                <Textarea placeholder="Not included (separate with |)" value={newEvent.exclusions || ""} onChange={(e) => setNewEvent({ ...newEvent, exclusions: e.target.value })} data-testid="new-event-exclusions" className="rounded-none bg-transparent border-white/10 min-h-14" />
                <label className="flex items-center gap-2 text-sm text-neutral-300"><input type="checkbox" checked={!!newEvent.price_on_request} onChange={(e) => setNewEvent({ ...newEvent, price_on_request: e.target.checked })} data-testid="new-event-on-request" /> Rates on request (hide the per-guest price)</label>
                <label className="flex items-center gap-2 text-sm text-neutral-300"><input type="checkbox" checked={newEvent.featured} onChange={(e) => setNewEvent({ ...newEvent, featured: e.target.checked })} /> Featured (signature evening)</label>
                <Button onClick={createEvent} data-testid="create-event-btn" className="w-full rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px]">Publish table</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* BOOKINGS */}
        <TabsContent value="bookings" className="mt-8">
          <div className="flex justify-end mb-4">
            <button onClick={() => exportCsv("bookings")} data-testid="export-bookings-csv"
              className="border border-white/10 hover:border-[#F5B041]/50 px-4 h-9 small-caps text-[10px] inline-flex items-center gap-2 transition">
              <Download size={13} /> Export CSV
            </button>
          </div>
          <div className="space-y-4" data-testid="admin-bookings">
            {bookings.map((b) => (
              <div key={b.id} className="card-lux p-5">
                <div className="grid md:grid-cols-6 gap-4 items-center">
                  <div className="md:col-span-2">
                    <div className="small-caps text-[10px] text-neutral-500">{b.service_type.replace("_"," ")}</div>
                    <div className="font-serif-menu text-lg">{b.customer_name || "—"}</div>
                    <div className="text-xs text-[#C9962F] mt-0.5" data-testid={`booking-phone-${b.id}`}>{b.customer_phone || "No contact number"}</div>
                    <div className="text-xs text-neutral-500 mt-1">{b.city} · {b.start_date || b.event_date}{b.guests ? ` · ${b.guests} guests` : ""}</div>
                    {(b.dietary_preferences?.length > 0 || b.allergies?.length > 0) && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {b.dietary_preferences?.slice(0, 3).map(d => <span key={d} className="chip text-[#F5B041] text-[9px]">{d}</span>)}
                        {b.allergies?.slice(0, 3).map(a => <span key={a} className="chip text-[#C04000] border-[#C04000]/40 text-[9px]">{a}</span>)}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="small-caps text-[9px] text-neutral-500">Chef</div>
                    <div className="text-sm">{b.chef_name || "—"}</div>
                  </div>
                  <div>
                    <div className="small-caps text-[9px] text-neutral-500">Total</div>
                    <div className="text-sm">₹{formatINR(b.total_amount)}</div>
                  </div>
                  <div>
                    <span className={`chip capitalize`}>{b.status.replace("_"," ")}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {!b.chef_id && !b.assigned_chef_id && (
                      <Select onValueChange={(v) => assignChef(b.id, v)}>
                        <SelectTrigger className="rounded-none bg-transparent border-white/10 h-9 text-xs" data-testid={`assign-${b.id}`}>
                          <SelectValue placeholder="Assign chef" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none bg-[#141414] border-white/10">
                          {chefs.filter(c => c.status === "approved" && c.active !== false).map(c => (
                            <SelectItem key={c.id} value={c.id} className="rounded-none">{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <button onClick={() => setSelectedBooking(b)} data-testid={`view-booking-${b.id}`}
                      className="border border-white/10 hover:bg-white/5 h-8 small-caps text-[10px] transition">
                      View brief
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* CRM / LEADS */}
        {/* CALENDAR */}
        <TabsContent value="calendar" className="mt-8" data-testid="admin-calendar">
          {(() => {
            const byDate = bookingsByDate();
            const y = calMonth.getFullYear(), mo = calMonth.getMonth();
            const startDow = new Date(y, mo, 1).getDay();
            const daysInMonth = new Date(y, mo + 1, 0).getDate();
            const pad = (n) => String(n).padStart(2, "0");
            const cells = [];
            for (let i = 0; i < startDow; i++) cells.push(null);
            for (let d = 1; d <= daysInMonth; d++) cells.push(d);
            return (
              <div>
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setCalMonth(new Date(y, mo - 1, 1))} data-testid="cal-prev" className="border border-white/10 p-2 hover:border-[#F5B041]/50"><ChevronLeft size={16} /></button>
                    <div className="font-serif-display text-2xl w-52 text-center">{calMonth.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</div>
                    <button onClick={() => setCalMonth(new Date(y, mo + 1, 1))} data-testid="cal-next" className="border border-white/10 p-2 hover:border-[#F5B041]/50"><ChevronRight size={16} /></button>
                  </div>
                  <Button onClick={() => setShowManual(true)} data-testid="add-manual-booking" className="rounded-none bg-[#C04000] hover:bg-[#A03500] h-10 small-caps text-[11px]"><Plus size={13} className="mr-1" /> Add booking</Button>
                </div>
                <div className="grid grid-cols-7 gap-px bg-white/10 border border-white/10">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                    <div key={d} className="bg-[#0F0F0F] text-center small-caps text-[10px] text-neutral-500 py-2">{d}</div>
                  ))}
                  {cells.map((d, i) => {
                    const ds = d ? `${y}-${pad(mo + 1)}-${pad(d)}` : null;
                    const items = ds ? (byDate[ds] || []) : [];
                    return (
                      <div key={i} className={`bg-[#0F0F0F] min-h-[96px] p-1.5 ${d ? "cursor-pointer hover:bg-white/5 transition" : ""}`}
                        onClick={() => d && setCalDay(ds)}
                        data-testid={d ? `cal-day-${ds}` : undefined}>
                        {d && (
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11px] text-neutral-500">{d}</span>
                            {items.length > 0 && <span className="text-[9px] text-[#F5B041]">{items.length}</span>}
                          </div>
                        )}
                        <div className="space-y-1">
                          {items.slice(0, 3).map((b) => (
                            <div key={b.id} title={`${b.customer_name || ""} · ${b.chef_name || b.menu_title || b.service_type}`}
                              className={`text-[9px] px-1 py-0.5 truncate border-l-2 ${b.service_type === "private_chef" ? "border-[#F5B041] bg-[#F5B041]/10" : b.service_type === "chefs_table" ? "border-[#C04000] bg-[#C04000]/10" : "border-emerald-500 bg-emerald-500/10"}`}>
                              {b.customer_name || b.chef_name || b.menu_title || "Booking"}
                            </div>
                          ))}
                          {items.length > 3 && <div className="text-[9px] text-neutral-500">+{items.length - 3} more</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-4 text-[10px] text-neutral-500">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#F5B041]" /> Private chef</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500" /> Catering</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#C04000]" /> Chef's Table</span>
                </div>
              </div>
            );
          })()}
        </TabsContent>

        {/* BOOKINGS placeholder-anchor */}
        <TabsContent value="crm" className="mt-8">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {["all", "phone_call", "callback", "concierge_chat", "membership"].map((s) => (
              <button
                key={s}
                onClick={() => setLeadSource(s)}
                data-testid={`crm-filter-${s}`}
                className={`px-3 py-1.5 small-caps text-[10px] border transition-colors ${
                  leadSource === s ? "border-[#C9962F] text-[#E7D8B5] bg-[#C9962F]/10" : "border-white/10 text-neutral-400 hover:text-white"
                }`}
              >
                {s === "all" ? "All" : s === "phone_call" ? "Phone Calls" : s === "concierge_chat" ? "Concierge" : s.replace(/^\w/, (c) => c.toUpperCase())}
              </button>
            ))}
            <span className="ml-auto small-caps text-[10px] text-neutral-500">
              {leads.filter((l) => leadSource === "all" || l.source === leadSource).length} leads
            </span>
            <button onClick={() => exportCsv("leads", leadSource === "all" ? {} : { source: leadSource })} data-testid="export-leads-csv"
              className="border border-white/10 hover:border-[#F5B041]/50 px-4 h-8 small-caps text-[10px] inline-flex items-center gap-2 transition">
              <Download size={13} /> Export CSV
            </button>
          </div>
          <div className="border border-white/10" data-testid="admin-crm">
            <div className="grid grid-cols-6 gap-4 px-5 py-3 small-caps text-[10px] text-neutral-500 border-b border-white/10">
              <div>Contact</div><div>Source</div><div className="col-span-2">Detail</div><div>Received</div><div>Status</div>
            </div>
            {leads.filter((l) => leadSource === "all" || l.source === leadSource).length === 0 && (
              <div className="px-5 py-10 text-center text-neutral-500 text-sm">No leads yet.</div>
            )}
            {leads.filter((l) => leadSource === "all" || l.source === leadSource).map((l) => (
              <div key={l.id} className="px-5 py-4 border-b border-white/5 text-sm">
                <div className="grid grid-cols-6 gap-4 items-center">
                  <div>
                    <div className="text-neutral-200">{l.name || "—"}</div>
                    <div className="text-neutral-500 text-xs">{l.phone || l.email || "—"}</div>
                    {l.city ? <div className="text-neutral-600 text-[11px]">{l.city}</div> : null}
                  </div>
                  <div><span className="chip capitalize">{(l.source || "").replace("_", " ")}</span></div>
                  <div className="col-span-2 text-neutral-400 text-xs truncate" title={l.message}>{l.message || "—"}</div>
                  <div className="text-neutral-500 text-xs">
                    <div>{(l.created_at || "").slice(0, 10)}</div>
                    {l.phone && l.phone.replace(/\D/g, "").length >= 10 && (
                      <a href={waLink(l)} target="_blank" rel="noreferrer" data-testid={`lead-whatsapp-${l.id}`}
                        className="inline-flex items-center gap-1 mt-1 text-[#25D366] hover:underline small-caps text-[10px]">
                        <MessageCircle size={12} /> WhatsApp
                      </a>
                    )}
                  </div>
                  <div>
                    <Select value={l.status} onValueChange={(v) => changeLeadStatus(l.id, v)}>
                      <SelectTrigger data-testid={`lead-status-${l.id}`} className="rounded-none bg-transparent border-white/10 h-9 text-xs capitalize">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-[#141414] border-white/10">
                        {["new", "contacted", "converted", "closed"].map((s) => (
                          <SelectItem key={s} value={s} className="rounded-none capitalize">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <LeadNote lead={l} onSave={saveLeadNote} />
              </div>
            ))}
          </div>
        </TabsContent>

        {/* MEMBERSHIPS */}
        <TabsContent value="memberships" className="mt-8">
          <div className="border border-white/10" data-testid="admin-memberships">
            <div className="grid grid-cols-6 gap-4 px-5 py-3 small-caps text-[10px] text-neutral-500 border-b border-white/10">
              <div>Applicant</div><div>Contact</div><div>Tier</div><div className="col-span-1">Note</div><div>Received</div><div>Status</div>
            </div>
            {memberships.length === 0 && (
              <div className="px-5 py-10 text-center text-neutral-500 text-sm">No membership requests yet.</div>
            )}
            {memberships.map((m) => (
              <div key={m.id} className="grid grid-cols-6 gap-4 px-5 py-4 border-b border-white/5 items-center text-sm">
                <div className="text-neutral-200">{m.name}</div>
                <div className="text-neutral-400 text-xs">
                  <div>{m.email}</div>
                  <div>{m.phone}</div>
                </div>
                <div><span className="chip">{m.tier}</span></div>
                <div className="text-neutral-400 text-xs truncate" title={m.message}>{m.message || "—"}</div>
                <div className="text-neutral-500 text-xs">{(m.created_at || "").slice(0, 10)}</div>
                <div>
                  <Select value={m.status} onValueChange={(v) => changeMembershipStatus(m.id, v)}>
                    <SelectTrigger data-testid={`membership-status-${m.id}`} className="rounded-none bg-transparent border-white/10 h-9 text-xs capitalize">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#141414] border-white/10">
                      {["new","contacted","approved","declined"].map(s => (
                        <SelectItem key={s} value={s} className="rounded-none capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="settings" className="mt-8" data-testid="admin-settings">
          <div className="max-w-2xl border border-white/10 p-7">
            <div className="small-caps text-[10px] text-neutral-500">Invoice &amp; agreement details</div>
            <h3 className="font-serif-display text-2xl mt-1">Billing identity</h3>
            <p className="text-neutral-500 text-xs mt-2">Printed on every GST invoice and signed agreement PDF.</p>
            <div className="dotted-sep my-6" />
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                ["legal_name", "Registered legal name"],
                ["gstin", "GSTIN"],
                ["email", "Billing email"],
                ["phone", "Billing phone"],
                ["invoice_prefix", "Invoice number prefix"],
                ["agreement_prefix", "Agreement number prefix"],
              ].map(([k, label]) => (
                <div key={k}>
                  <Label className="small-caps text-[10px] text-neutral-500">{label}</Label>
                  <Input value={settings[k] || ""} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })}
                    data-testid={`settings-${k}`} className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                </div>
              ))}
              <div className="sm:col-span-2">
                <Label className="small-caps text-[10px] text-neutral-500">Registered billing address</Label>
                <Textarea value={settings.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  data-testid="settings-address" className="mt-2 rounded-none bg-transparent border-white/10 min-h-24" />
              </div>
            </div>
            <Button onClick={saveSettings} disabled={settingsSaving} data-testid="save-settings"
              className="mt-6 rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px]">
              {settingsSaving ? "Saving…" : "Save settings"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-8">
          <div className="border border-white/10" data-testid="admin-users">
            <div className="grid grid-cols-4 gap-4 px-5 py-3 small-caps text-[10px] text-neutral-500 border-b border-white/10">
              <div>User</div><div>Contact</div><div>Role</div><div>Actions</div>
            </div>
            {users.map((u) => (
              <div key={u.id} className="grid grid-cols-4 gap-4 px-5 py-4 border-b border-white/5 items-center">
                <div>{u.name}</div>
                <div className="text-sm text-neutral-400">{u.email || u.phone}</div>
                <div><span className="chip capitalize">{u.role}</span></div>
                <div>
                  <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                    <SelectTrigger className="rounded-none bg-transparent border-white/10 h-9 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#141414] border-white/10">
                      {["customer","chef","manager","admin"].map(r => (
                        <SelectItem key={r} value={r} className="rounded-none capitalize">{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* ============ Menu Edit Dialog ============ */}
      <Dialog open={!!editingMenu} onOpenChange={(o) => { if (!o) setEditingMenu(null); }}>
        <DialogContent className="max-w-lg bg-[#0F0F0F] border border-[#C9962F]/30 rounded-none max-h-[85vh] overflow-y-auto" data-testid="menu-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-white">Edit menu</DialogTitle>
          </DialogHeader>
          {menuEditForm && (
            <div className="space-y-3 mt-4">
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Title</Label>
                <Input value={menuEditForm.title} onChange={(e) => setMenuEditForm({ ...menuEditForm, title: e.target.value })} data-testid="edit-menu-title" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Tagline</Label>
                <Input value={menuEditForm.tagline} onChange={(e) => setMenuEditForm({ ...menuEditForm, tagline: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Category</Label>
                <Input value={menuEditForm.category} onChange={(e) => setMenuEditForm({ ...menuEditForm, category: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500 block mb-2">Image</Label>
                <ImageUpload value={menuEditForm.image_url} onChange={(url) => setMenuEditForm({ ...menuEditForm, image_url: url })} testId="edit-menu-image" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Description</Label>
                <Textarea value={menuEditForm.description} onChange={(e) => setMenuEditForm({ ...menuEditForm, description: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 min-h-20" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Courses (comma-separated)</Label>
                <Textarea value={menuEditForm.courses} onChange={(e) => setMenuEditForm({ ...menuEditForm, courses: e.target.value })} data-testid="edit-menu-courses" className="mt-1 rounded-none bg-transparent border-white/10 min-h-20" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Chef credit</Label>
                <Input value={menuEditForm.chef_credit} onChange={(e) => setMenuEditForm({ ...menuEditForm, chef_credit: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Price/head</Label>
                  <Input type="number" value={menuEditForm.price_per_head} onChange={(e) => setMenuEditForm({ ...menuEditForm, price_per_head: e.target.value })} data-testid="edit-menu-price" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
                </div>
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Min guests</Label>
                  <Input type="number" value={menuEditForm.min_guests} onChange={(e) => setMenuEditForm({ ...menuEditForm, min_guests: e.target.value })} data-testid="edit-menu-min-guests" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
                </div>
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Tags (comma-separated)</Label>
                <Input value={menuEditForm.tags} onChange={(e) => setMenuEditForm({ ...menuEditForm, tags: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-300 pt-1">
                <input type="checkbox" checked={menuEditForm.featured} onChange={(e) => setMenuEditForm({ ...menuEditForm, featured: e.target.checked })} data-testid="edit-menu-featured" />
                Featured
              </label>
              <div className="flex gap-3 pt-2">
                <Button onClick={saveMenuEdit} disabled={menuEditSaving} data-testid="save-menu-edit" className="flex-1 rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px]">
                  {menuEditSaving ? "Saving…" : "Save changes"}
                </Button>
                <Button onClick={() => setEditingMenu(null)} variant="outline" className="rounded-none border-white/10 h-11 small-caps text-[11px]">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ Chef Edit Dialog ============ */}
      <Dialog open={!!editingChef} onOpenChange={(o) => { if (!o) setEditingChef(null); }}>
        <DialogContent className="max-w-3xl bg-[#0F0F0F] border-white/10 rounded-none p-0 max-h-[92vh] overflow-y-auto" data-testid="chef-edit-dialog">
          <div className="p-8">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-3xl">Edit chef profile</DialogTitle>
              <p className="text-neutral-400 text-sm mt-1">Update details, salary, roster, and visibility.</p>
            </DialogHeader>

            {chefEditForm && (
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div className="space-y-4">
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Name</Label>
                    <Input value={chefEditForm.name} onChange={(e) => setChefEditForm({ ...chefEditForm, name: e.target.value })} data-testid="edit-chef-name"
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500 block mb-2">Profile photo</Label>
                    <ImageUpload value={chefEditForm.photo_url} onChange={(url) => setChefEditForm({ ...chefEditForm, photo_url: url })} testId="edit-chef-photo" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Bio</Label>
                    <Textarea value={chefEditForm.bio} onChange={(e) => setChefEditForm({ ...chefEditForm, bio: e.target.value })}
                      className="mt-2 rounded-none bg-transparent border-white/10 min-h-24" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Cuisines (comma-separated)</Label>
                    <Input value={chefEditForm.cuisines} onChange={(e) => setChefEditForm({ ...chefEditForm, cuisines: e.target.value })}
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Signature dishes</Label>
                    <Input value={chefEditForm.signature_dishes} onChange={(e) => setChefEditForm({ ...chefEditForm, signature_dishes: e.target.value })}
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Languages</Label>
                    <Input value={chefEditForm.languages} onChange={(e) => setChefEditForm({ ...chefEditForm, languages: e.target.value })}
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="small-caps text-[10px] text-neutral-500">Experience (yrs)</Label>
                      <Input type="number" value={chefEditForm.experience_yrs} onChange={(e) => setChefEditForm({ ...chefEditForm, experience_yrs: e.target.value })}
                        className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                    </div>
                    <div>
                      <Label className="small-caps text-[10px] text-neutral-500">Rating</Label>
                      <Input type="number" step="0.1" min="0" max="5" value={chefEditForm.rating} onChange={(e) => setChefEditForm({ ...chefEditForm, rating: e.target.value })}
                        className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                    </div>
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Roster</Label>
                    <Select value={chefEditForm.salary_type} onValueChange={(v) => setChefEditForm({ ...chefEditForm, salary_type: v })}>
                      <SelectTrigger className="mt-2 rounded-none bg-transparent border-white/10 h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-[#141414] border-white/10">
                        <SelectItem value="indian" className="rounded-none">India (₹50k–₹1.25L)</SelectItem>
                        <SelectItem value="international" className="rounded-none">International (₹90k–₹3.55L)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Monthly salary (₹)</Label>
                    <Input type="number" value={chefEditForm.monthly_salary} onChange={(e) => setChefEditForm({ ...chefEditForm, monthly_salary: e.target.value })} data-testid="edit-chef-salary"
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Trial fee per day (₹)</Label>
                    <Input type="number" value={chefEditForm.trial_fee} onChange={(e) => setChefEditForm({ ...chefEditForm, trial_fee: e.target.value })} data-testid="edit-chef-trial-fee"
                      placeholder="0 = default (2500 India / 5000 international)"
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Cities</Label>
                    <Input value={chefEditForm.cities} onChange={(e) => setChefEditForm({ ...chefEditForm, cities: e.target.value })}
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Diet tags</Label>
                    <Input value={chefEditForm.diet_tags} onChange={(e) => setChefEditForm({ ...chefEditForm, diet_tags: e.target.value })}
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Gallery (image URLs, comma-separated)</Label>
                    <Input value={chefEditForm.gallery} onChange={(e) => setChefEditForm({ ...chefEditForm, gallery: e.target.value })}
                      className="mt-2 rounded-none bg-transparent border-white/10 h-11" />
                  </div>
                  <div className="flex items-center justify-between border border-white/10 px-4 py-3">
                    <div>
                      <div className="small-caps text-[10px] text-neutral-500">Publicly visible</div>
                      <div className="text-xs text-neutral-400 mt-1">Off = hide from customer roster.</div>
                    </div>
                    <Switch checked={!!chefEditForm.active} onCheckedChange={(v) => setChefEditForm({ ...chefEditForm, active: v })} data-testid="edit-chef-active" />
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-8 pt-4 border-t border-white/10">
              <Button variant="ghost" onClick={() => setEditingChef(null)}
                className="flex-1 rounded-none border border-white/10 h-11 small-caps text-[11px] hover:bg-white/5">Cancel</Button>
              <Button onClick={saveChefEdit} disabled={chefEditSaving} data-testid="save-chef-edit"
                className="flex-[2] rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px]">Save changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ Chef Appointments Dialog ============ */}
      <Dialog open={!!chefAppointmentsOpen} onOpenChange={(o) => { if (!o) { setChefAppointmentsOpen(null); setChefAppointments([]); }}}>
        <DialogContent className="max-w-3xl bg-[#0F0F0F] border-white/10 rounded-none p-0 max-h-[90vh] overflow-y-auto" data-testid="chef-appts-dialog">
          <div className="p-8">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-3xl">
                {chefAppointmentsOpen?.name} — Appointments
              </DialogTitle>
              <p className="text-neutral-400 text-sm mt-1">All bookings assigned to or requested for this chef.</p>
            </DialogHeader>
            <div className="mt-6 space-y-3">
              {chefAppointments.length === 0 ? (
                <div className="text-neutral-500 text-center py-12">No appointments yet.</div>
              ) : chefAppointments.map((b) => (
                <div key={b.id} className="border border-white/10 p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-serif-menu">{b.customer_name}</div>
                      <div className="text-xs text-[#C9962F] mt-0.5">{b.customer_phone || "No contact number"}</div>
                      <div className="text-xs text-neutral-500 mt-1">
                        {b.service_type.replace("_"," ")} · {b.city} · {b.start_date || b.event_date} · ₹{formatINR(b.total_amount)}
                      </div>
                    </div>
                    <span className="chip capitalize">{b.status.replace("_"," ")}</span>
                  </div>
                  {(b.dietary_preferences?.length > 0 || b.allergies?.length > 0) && (
                    <div className="mt-3 text-xs space-y-1">
                      {b.dietary_preferences?.length > 0 && <div><span className="text-neutral-500">Diet: </span><span className="text-[#F5B041]">{b.dietary_preferences.join(", ")}</span></div>}
                      {b.allergies?.length > 0 && <div><span className="text-neutral-500">Allergies: </span><span className="text-[#C04000]">{b.allergies.join(", ")}</span></div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ Calendar Day Dialog ============ */}
      <Dialog open={!!calDay} onOpenChange={(o) => { if (!o) setCalDay(null); }}>
        <DialogContent className="max-w-3xl bg-[#0F0F0F] border-white/10 rounded-none p-0 max-h-[90vh] overflow-y-auto" data-testid="cal-day-dialog">
          {calDay && (() => {
            const dayItems = bookingsByDate()[calDay] || [];
            return (
              <div className="p-8">
                <DialogHeader>
                  <DialogTitle className="font-serif-display text-3xl">
                    {new Date(`${calDay}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                  </DialogTitle>
                  <p className="text-neutral-400 text-sm mt-1" data-testid="cal-day-count">
                    {dayItems.length} booking{dayItems.length === 1 ? "" : "s"} on this day
                  </p>
                </DialogHeader>
                {dayItems.length === 0 && (
                  <div className="mt-8 text-center text-neutral-500 text-sm border border-white/10 py-10">Nothing scheduled.</div>
                )}
                <div className="mt-6 space-y-4">
                  {dayItems.map((b) => (
                    <div key={b.id} className="border border-white/10 p-5" data-testid={`cal-day-booking-${b.id}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <div className="small-caps text-[10px] text-neutral-500 capitalize">{b.service_type.replace("_", " ")}</div>
                          <div className="font-serif-menu text-lg">{b.customer_name || "—"}</div>
                          <div className="text-xs text-[#C9962F] mt-0.5">{b.customer_phone || "No contact number"}</div>
                        </div>
                        <div className="text-right">
                          <div className="small-caps text-[10px] text-neutral-500">Amount</div>
                          <div className="font-serif-menu text-lg">₹{formatINR(b.total_amount)}</div>
                        </div>
                      </div>
                      <div className="dotted-sep my-4" />
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div><div className="small-caps text-[10px] text-neutral-500">City</div><div>{b.city || "—"}</div></div>
                        <div><div className="small-caps text-[10px] text-neutral-500">Chef</div><div>{b.chef_name || "Unassigned"}</div></div>
                        <div><div className="small-caps text-[10px] text-neutral-500">Menu / Event</div><div>{b.menu_title || b.event_title || "—"}</div></div>
                        <div><div className="small-caps text-[10px] text-neutral-500">Guests</div><div>{b.guests || "—"}</div></div>
                      </div>
                      <div className="flex items-center gap-3 mt-5 flex-wrap">
                        <Select value={b.status} onValueChange={(v) => changeBookingStatus(b.id, v)}>
                          <SelectTrigger data-testid={`cal-status-${b.id}`} className="rounded-none bg-transparent border-white/10 h-9 text-xs capitalize w-44">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none bg-[#141414] border-white/10">
                            {["pending", "assigned", "confirmed", "paid", "in_progress", "completed", "cancelled"].map((s) => (
                              <SelectItem key={s} value={s} className="rounded-none capitalize">{s.replace("_", " ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <button onClick={() => { setCalDay(null); setSelectedBooking(b); }} data-testid={`cal-view-${b.id}`}
                          className="border border-white/10 hover:bg-white/5 px-4 h-9 small-caps text-[10px] transition">
                          Full brief
                        </button>
                        {b.customer_phone && (
                          <a href={waLink({ phone: b.customer_phone, name: b.customer_name, message: (b.service_type || "").replace("_", " ") })}
                            target="_blank" rel="noreferrer" data-testid={`cal-whatsapp-${b.id}`}
                            className="inline-flex items-center gap-1 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 px-4 h-9 small-caps text-[10px] transition">
                            <MessageCircle size={12} /> WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ============ Booking Detail Dialog ============ */}
      <Dialog open={!!selectedBooking} onOpenChange={(o) => { if (!o) setSelectedBooking(null); }}>
        <DialogContent className="max-w-2xl bg-[#0F0F0F] border-white/10 rounded-none p-0 max-h-[90vh] overflow-y-auto" data-testid="booking-detail-dialog">
          {selectedBooking && (
            <div className="p-8">
              <DialogHeader>
                <DialogTitle className="font-serif-display text-3xl">Booking · {selectedBooking.customer_name}</DialogTitle>
                <p className="text-neutral-400 text-sm mt-1 capitalize">{selectedBooking.service_type.replace("_"," ")}</p>
              </DialogHeader>
              <div className="mt-6 space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div data-testid="detail-contact-name"><div className="small-caps text-[10px] text-neutral-500">Customer</div><div>{selectedBooking.customer_name || "—"}</div></div>
                  <div data-testid="detail-contact-phone"><div className="small-caps text-[10px] text-neutral-500">Contact number</div><div className="text-[#C9962F]">{selectedBooking.customer_phone || "—"}</div></div>
                  <div><div className="small-caps text-[10px] text-neutral-500">City</div><div>{selectedBooking.city}</div></div>
                  <div><div className="small-caps text-[10px] text-neutral-500">Date</div><div>{selectedBooking.start_date || selectedBooking.event_date}</div></div>
                  {selectedBooking.guests ? <div><div className="small-caps text-[10px] text-neutral-500">Guests</div><div>{selectedBooking.guests}</div></div> : null}
                  <div><div className="small-caps text-[10px] text-neutral-500">Chef</div><div>{selectedBooking.chef_name || "Unassigned"}</div></div>
                  <div><div className="small-caps text-[10px] text-neutral-500">Total</div><div>₹{formatINR(selectedBooking.total_amount)}</div></div>
                  <div>
                    <div className="small-caps text-[10px] text-neutral-500">Status</div>
                    <Select value={selectedBooking.status} onValueChange={(v) => { changeBookingStatus(selectedBooking.id, v); setSelectedBooking({ ...selectedBooking, status: v }); }}>
                      <SelectTrigger data-testid={`detail-status-${selectedBooking.id}`} className="rounded-none bg-transparent border-white/10 h-9 text-xs capitalize mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-[#141414] border-white/10">
                        {["pending", "assigned", "confirmed", "paid", "in_progress", "completed", "cancelled"].map((s) => (
                          <SelectItem key={s} value={s} className="rounded-none capitalize">{s.replace("_", " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {selectedBooking.dietary_preferences?.length > 0 && (
                  <div><div className="small-caps text-[10px] text-neutral-500 mb-1">Dietary preferences</div>
                    <div className="flex flex-wrap gap-1">{selectedBooking.dietary_preferences.map(d => <span key={d} className="chip text-[#F5B041]">{d}</span>)}</div></div>
                )}
                {selectedBooking.allergies?.length > 0 && (
                  <div><div className="small-caps text-[10px] text-neutral-500 mb-1">Allergies</div>
                    <div className="flex flex-wrap gap-1">{selectedBooking.allergies.map(a => <span key={a} className="chip text-[#C04000] border-[#C04000]/40">{a}</span>)}</div></div>
                )}
                {selectedBooking.chef_notes && (
                  <div><div className="small-caps text-[10px] text-neutral-500 mb-1">Chef notes</div>
                    <div className="text-neutral-300">{selectedBooking.chef_notes}</div></div>
                )}
                {selectedBooking.kitchen_equipment?.length > 0 && (
                  <div><div className="small-caps text-[10px] text-neutral-500 mb-1">Kitchen equipment on-site</div>
                    <div className="flex flex-wrap gap-1">{selectedBooking.kitchen_equipment.map(k => <span key={k} className="chip">{k}</span>)}</div></div>
                )}
                {selectedBooking.custom_request && (
                  <div><div className="small-caps text-[10px] text-neutral-500 mb-1">Brief</div><div className="text-neutral-300">{selectedBooking.custom_request}</div></div>
                )}
                {selectedBooking.notes && (
                  <div><div className="small-caps text-[10px] text-neutral-500 mb-1">Notes</div><div className="text-neutral-300">{selectedBooking.notes}</div></div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* ============ Manual Booking Dialog ============ */}
      <Dialog open={showManual} onOpenChange={setShowManual}>
        <DialogContent className="max-w-lg bg-[#0F0F0F] border border-[#C9962F]/30 rounded-none max-h-[85vh] overflow-y-auto" data-testid="manual-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-white">Add booking (phone / walk-in)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <div>
              <Label className="small-caps text-[10px] text-neutral-500">Booking type</Label>
              <select value={manualForm.service_type} onChange={(e) => setManualForm({ ...manualForm, service_type: e.target.value })} data-testid="manual-service-type"
                className="mt-1 w-full rounded-none bg-transparent border border-white/10 h-11 px-3 text-sm">
                <option className="bg-[#0F0F0F]" value="private_chef">Private chef</option>
                <option className="bg-[#0F0F0F]" value="mini_catering">Mini catering / event</option>
                <option className="bg-[#0F0F0F]" value="chefs_table">Chef's Table</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Customer name</Label>
                <Input value={manualForm.customer_name} onChange={(e) => setManualForm({ ...manualForm, customer_name: e.target.value })} data-testid="manual-name" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Contact number</Label>
                <Input value={manualForm.customer_phone} onChange={(e) => setManualForm({ ...manualForm, customer_phone: e.target.value })} data-testid="manual-phone" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Date</Label>
                <Input type="date" value={manualForm.date} onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })} data-testid="manual-date" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">City</Label>
                <Input value={manualForm.city} onChange={(e) => setManualForm({ ...manualForm, city: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
            </div>
            {manualForm.service_type === "private_chef" && (
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Chef (optional)</Label>
                <select value={manualForm.chef_id} onChange={(e) => setManualForm({ ...manualForm, chef_id: e.target.value })} className="mt-1 w-full rounded-none bg-transparent border border-white/10 h-11 px-3 text-sm">
                  <option className="bg-[#0F0F0F]" value="">— none —</option>
                  {chefs.map((c) => <option className="bg-[#0F0F0F]" key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {manualForm.service_type === "mini_catering" && (
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Menu (optional)</Label>
                <select value={manualForm.menu_id} onChange={(e) => setManualForm({ ...manualForm, menu_id: e.target.value })} className="mt-1 w-full rounded-none bg-transparent border border-white/10 h-11 px-3 text-sm">
                  <option className="bg-[#0F0F0F]" value="">— none —</option>
                  {menus.map((mn) => <option className="bg-[#0F0F0F]" key={mn.id} value={mn.id}>{mn.title}</option>)}
                </select>
              </div>
            )}
            {manualForm.service_type === "chefs_table" && (
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Event (optional)</Label>
                <select value={manualForm.event_table_id} onChange={(e) => setManualForm({ ...manualForm, event_table_id: e.target.value })} className="mt-1 w-full rounded-none bg-transparent border border-white/10 h-11 px-3 text-sm">
                  <option className="bg-[#0F0F0F]" value="">— none —</option>
                  {events.map((ev) => <option className="bg-[#0F0F0F]" key={ev.id} value={ev.id}>{ev.title}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Guests</Label>
                <Input type="number" value={manualForm.guests} onChange={(e) => setManualForm({ ...manualForm, guests: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Amount ₹</Label>
                <Input type="number" value={manualForm.total_amount} onChange={(e) => setManualForm({ ...manualForm, total_amount: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Status</Label>
                <select value={manualForm.status} onChange={(e) => setManualForm({ ...manualForm, status: e.target.value })} className="mt-1 w-full rounded-none bg-transparent border border-white/10 h-11 px-2 text-sm">
                  <option className="bg-[#0F0F0F]" value="confirmed">Confirmed</option>
                  <option className="bg-[#0F0F0F]" value="pending">Pending</option>
                  <option className="bg-[#0F0F0F]" value="assigned">Assigned</option>
                  <option className="bg-[#0F0F0F]" value="paid">Paid</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="small-caps text-[10px] text-neutral-500">Notes</Label>
              <Textarea value={manualForm.notes} onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })} data-testid="manual-notes" className="mt-1 rounded-none bg-transparent border-white/10 min-h-16" />
              <div className="mt-2">
                <DictateButton testId="manual-notes-dictate" label="Dictate notes"
                  context="A Chef Hire concierge recording an offline or phone booking — menu, guests, date, city and special requests."
                  onText={(t) => setManualForm((f) => ({ ...f, notes: [f.notes.trim(), t].filter(Boolean).join(" ") }))} />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={submitManual} disabled={manualSaving} data-testid="save-manual-booking" className="flex-1 rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px]">{manualSaving ? "Saving…" : "Add booking"}</Button>
              <Button onClick={() => setShowManual(false)} variant="outline" className="rounded-none border-white/10 h-11 small-caps text-[11px]">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ Chef's Table Edit Dialog ============ */}
      <Dialog open={!!editingEvent} onOpenChange={(o) => { if (!o) setEditingEvent(null); }}>
        <DialogContent className="max-w-lg bg-[#0F0F0F] border border-[#C9962F]/30 rounded-none max-h-[85vh] overflow-y-auto" data-testid="event-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-white">Edit Chef's Table</DialogTitle>
          </DialogHeader>
          {eventEditForm && (
            <div className="space-y-3 mt-4">
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Title</Label>
                <Input value={eventEditForm.title} onChange={(e) => setEventEditForm({ ...eventEditForm, title: e.target.value })} data-testid="edit-event-title" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Tagline</Label>
                <Input value={eventEditForm.tagline} onChange={(e) => setEventEditForm({ ...eventEditForm, tagline: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500 block mb-2">Image</Label>
                <ImageUpload value={eventEditForm.image_url} onChange={(url) => setEventEditForm({ ...eventEditForm, image_url: url })} testId="edit-event-image" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Description</Label>
                <Textarea value={eventEditForm.description} onChange={(e) => setEventEditForm({ ...eventEditForm, description: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 min-h-20" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Courses (comma-separated)</Label>
                <Textarea value={eventEditForm.courses} onChange={(e) => setEventEditForm({ ...eventEditForm, courses: e.target.value })} data-testid="edit-event-courses" className="mt-1 rounded-none bg-transparent border-white/10 min-h-20" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Chef credit</Label>
                <Input value={eventEditForm.chef_credit} onChange={(e) => setEventEditForm({ ...eventEditForm, chef_credit: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Event date</Label>
                  <Input type="date" value={eventEditForm.event_date} onChange={(e) => setEventEditForm({ ...eventEditForm, event_date: e.target.value })} data-testid="edit-event-date" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
                </div>
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Seating time</Label>
                  <Input value={eventEditForm.event_time} onChange={(e) => setEventEditForm({ ...eventEditForm, event_time: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Cover ₹/seat</Label>
                  <Input type="number" value={eventEditForm.cover_charge} onChange={(e) => setEventEditForm({ ...eventEditForm, cover_charge: e.target.value })} data-testid="edit-event-cover" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
                </div>
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Total seats</Label>
                  <Input type="number" value={eventEditForm.total_seats} onChange={(e) => setEventEditForm({ ...eventEditForm, total_seats: e.target.value })} data-testid="edit-event-seats" className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
                </div>
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Booked</Label>
                  <Input type="number" value={eventEditForm.seats_booked} onChange={(e) => setEventEditForm({ ...eventEditForm, seats_booked: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
                </div>
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Inclusions (separate with |)</Label>
                <Textarea value={eventEditForm.inclusions} onChange={(e) => setEventEditForm({ ...eventEditForm, inclusions: e.target.value })}
                  data-testid="edit-event-inclusions" className="mt-1 rounded-none bg-transparent border-white/10 min-h-20" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Not included (separate with |)</Label>
                <Textarea value={eventEditForm.exclusions} onChange={(e) => setEventEditForm({ ...eventEditForm, exclusions: e.target.value })}
                  data-testid="edit-event-exclusions" className="mt-1 rounded-none bg-transparent border-white/10 min-h-16" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Dish gallery ({(eventEditForm.gallery || []).length} photos)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(eventEditForm.gallery || []).map((g, i) => (
                    <div key={g + i} className="relative">
                      <img src={g} alt="" className="w-16 h-16 object-cover border border-white/10" />
                      <button onClick={() => setEventEditForm({ ...eventEditForm, gallery: eventEditForm.gallery.filter((_, j) => j !== i) })}
                        data-testid={`remove-gallery-${i}`}
                        className="absolute -top-2 -right-2 bg-[#C04000] text-white w-5 h-5 text-xs leading-none">×</button>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <GalleryAdder testId="edit-event-gallery-upload"
                    onAdd={(url) => setEventEditForm((f) => ({ ...f, gallery: [...(f.gallery || []), url] }))} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-300">
                <input type="checkbox" checked={!!eventEditForm.price_on_request}
                  onChange={(e) => setEventEditForm({ ...eventEditForm, price_on_request: e.target.checked })}
                  data-testid="edit-event-on-request" />
                Rates on request (hide the per-guest price)
              </label>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Tags (comma-separated)</Label>
                <Input value={eventEditForm.tags} onChange={(e) => setEventEditForm({ ...eventEditForm, tags: e.target.value })} className="mt-1 rounded-none bg-transparent border-white/10 h-11" />
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-300 pt-1">
                <input type="checkbox" checked={eventEditForm.featured} onChange={(e) => setEventEditForm({ ...eventEditForm, featured: e.target.checked })} data-testid="edit-event-featured" />
                Featured (signature evening)
              </label>
              <div className="flex gap-3 pt-2">
                <Button onClick={saveEventEdit} disabled={eventSaving} data-testid="save-event-edit" className="flex-1 rounded-none bg-[#C04000] hover:bg-[#A03500] h-11 small-caps text-[11px]">
                  {eventSaving ? "Saving…" : "Save changes"}
                </Button>
                <Button onClick={() => setEditingEvent(null)} variant="outline" className="rounded-none border-white/10 h-11 small-caps text-[11px]">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
